/**
 * Normaliser titler så de ikke råber.
 *
 * Nogle kilder (særligt medier som idoart.dk) leverer overskrifter i
 * BLOKBOGSTAVER. Det bryder Flâneriets rolige udtryk. Denne funktion
 * detekterer titler, hvor stort set alle bogstaver er versaler, og
 * skriver dem om til sætning + bevarer korte akronymer (DR, EU, USA, DK,
 * NATO, FN, BBC osv.).
 */

const ACRONYM_WHITELIST = new Set([
  "DR", "DK", "EU", "USA", "UK", "US", "FN", "NATO", "BBC", "CNN", "NYT",
  "MoMA", "MOMA", "TED", "NASA", "OL", "VM", "EM", "SF", "SFI", "SAS",
  "LEGO", "IKEA", "H&M", "P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8",
  "III", "IV", "VI", "VII", "VIII", "IX", "XL", "XX", "XXI", "II",
]);

function toSentenceWord(word: string, isFirst: boolean): string {
  if (!word) return word;
  // Bevar akronymer og korte specialtegn/tal
  const stripped = word.replace(/[^\p{L}\p{N}&]/gu, "");
  if (!stripped) return word.toLowerCase();
  if (ACRONYM_WHITELIST.has(stripped)) return word;
  // Tal og ren-symbol
  if (!/\p{L}/u.test(stripped)) return word;

  const lower = word.toLowerCase();
  if (isFirst) {
    // Kapitaliser første bogstav
    return lower.replace(/(\p{L})/u, (m) => m.toUpperCase());
  }
  return lower;
}

/**
 * Returnerer titlen skrevet i almindelig sætnings-case, hvis den originalt
 * er (næsten) helt i blokbogstaver. Ellers returneres originalen uændret.
 */
export function normalizeTitle(input: string | null | undefined): string {
  if (!input) return "";
  const s = input.trim();
  if (s.length < 8) return s;

  const letters = s.match(/\p{L}/gu) ?? [];
  if (letters.length < 6) return s;

  const upper = letters.filter((c) => c === c.toUpperCase() && c !== c.toLowerCase());
  const ratio = upper.length / letters.length;
  // Kun ind, hvis mindst 65% af bogstaverne er versaler → tydeligt "råber".
  // (Grænsen ligger over normal dansk prosa, der typisk har 5–15% versaler,
  // men lavt nok til at fange titler med en lille kilde-suffix i småt.)
  if (ratio < 0.65) return s;

  // Split på whitespace, bevar bindestreger og tegnsætning
  const parts = s.split(/(\s+)/);
  let wordIndex = 0;
  const result = parts.map((part) => {
    if (/^\s+$/.test(part)) return part;
    const sub = part.split(/([-––/])/); // bevar bindestreger som separatorer
    const rebuilt = sub
      .map((piece) => {
        if (/^[-––/]$/.test(piece)) return piece;
        const isFirst = wordIndex === 0;
        wordIndex += 1;
        return toSentenceWord(piece, isFirst);
      })
      .join("");
    return rebuilt;
  });

  // Efter sætningstegn (. ! ?) → kapitaliser næste ord
  let out = result.join("");
  out = out.replace(/([.!?]\s+)(\p{Ll})/gu, (_, sep, ch) => sep + ch.toUpperCase());
  return out;
}
